import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { PortType } from '~/graphql/graphql';
import type { RootState } from '~/redux/store';
import { portDisplay } from '~/utils';

type PinnedPort = {
  id: string;
  name: string;
  country?: string | null;
  type: PortType;
};

// US/Canada surfaced first, then the rest alphabetically. ISO-2 codes ranked
// alongside the display names so the fallback path keeps the same order.
const PRIORITY_PORT_GROUPS = ['United States', 'US', 'Canada', 'CA'];

function comparePortGroups(a: string, b: string): number {
  const aIndex = PRIORITY_PORT_GROUPS.indexOf(a);
  const bIndex = PRIORITY_PORT_GROUPS.indexOf(b);
  const aRank = aIndex === -1 ? Infinity : aIndex;
  const bRank = bIndex === -1 ? Infinity : bIndex;
  if (aRank !== bRank) return aRank - bRank;
  return a.localeCompare(b);
}

type PortSelectorProps = {
  name: string;
  label?: string;
  value: string;
  onChange: (name: string, value: string) => void;
  /** Defaults to seaports only. */
  types?: PortType[];
  /** A saved selection kept displayable even when filtered out. */
  pinned?: PinnedPort | null;
  formatLabel?: (name: string) => string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
};

/** Port picker backed by Redux reference data, grouped by country. */
export function PortSelector({
  name,
  label,
  value,
  onChange,
  types = [PortType.Seaport],
  pinned,
  formatLabel,
  disabled,
  required,
  placeholder,
}: PortSelectorProps) {
  const { i18n } = useTranslation();
  const ports = useSelector((state: RootState) => state.auth.ports);
  const language = i18n.language;

  const options = useMemo(() => {
    const filtered = ports.filter((port) => types.includes(port.type));
    // Keep a saved-but-now-filtered port displayable, but only if either (a) its
    // type matches the filter (e.g. an older Redux ports snapshot is missing it)
    // or (b) it's the currently-selected value — so the user sees what's stored
    // and can change it, without being offered a type-mismatched port as a new
    // pickable option (e.g. a saved seaport while types is [CY,RAMP,DOOR],
    // which the backend would reject at placeOrder).
    const shouldPin =
      pinned &&
      !filtered.some((port) => port.id === pinned.id) &&
      (types.includes(pinned.type) || pinned.id === value);
    const portsForOptions = shouldPin ? [...filtered, pinned] : filtered;

    const grouped = portsForOptions
      .map((port) => {
        const {
          label: displayName,
          group,
          groupSortKey,
        } = portDisplay(port, language);
        return {
          value: port.id,
          label: formatLabel ? formatLabel(displayName) : displayName,
          group,
          groupSortKey,
        };
      })
      .sort(
        (a, b) =>
          comparePortGroups(a.groupSortKey, b.groupSortKey) ||
          a.label.localeCompare(b.label)
      )
      .map(({ value: v, label: l, group }) => ({ value: v, label: l, group }));

    return [{ value: '', label: '—' }, ...grouped];
  }, [ports, pinned, types, value, formatLabel, language]);

  return (
    <SearchableSelect
      name={name}
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
    />
  );
}
